/**
 * Audio cue driver (spec 007): turns a cue list and a playhead into calls on
 * an audio port. SceneRuntime drives it with its clock; the editor preview
 * (`Editor/src/utils/previewAudio.ts`) drives it with the store playhead and a
 * browser port. The port does the sound; the driver only decides when.
 */

import { cueGainAt, type AudioCue } from '../utils/sceneTiming';

/** What the driver asks the port to start. */
export interface AudioPlayRequest extends AudioCue {
  /** How far into the media to start (ms), > 0 when the playhead is mid-cue. */
  offsetMs: number;
  /** Gain to start at, envelope applied. */
  initialGain: number;
}

/** The sound backend: a browser port in the editor, a fake in tests, absent = silent. */
export interface AudioPort<H = unknown> {
  play(cue: AudioPlayRequest): H;
  stop(handle: H): void;
  setGain(handle: H, gain: number): void;
}

export class AudioCueDriver<H = unknown> {
  private active = new Map<string, { cue: AudioCue; handle: H }>();
  private lastPlayhead: number | null = null;

  constructor(private readonly port: AudioPort<H>) {}

  /** Start, stop and re-gain cues for the playhead. */
  update(cues: AudioCue[], playheadMs: number): void {
    const last = this.lastPlayhead;
    // A playhead that moves backwards is a seek or a restart: start over.
    // Forward jumps (a throttled tab) keep running cues, which play in real time.
    if (last !== null && playheadMs < last) this.stopAll();
    this.lastPlayhead = playheadMs;

    const wanted = new Set<string>();
    for (const cue of cues) {
      if (playheadMs < cue.startMs || playheadMs >= cue.endMs) continue;
      wanted.add(cue.key);
      const gain = cueGainAt(cue, playheadMs);
      const running = this.active.get(cue.key);
      if (running && running.cue.mediaId === cue.mediaId && running.cue.loop === cue.loop) {
        running.cue = cue;
        this.safe(() => this.port.setGain(running.handle, gain));
        continue;
      }
      if (running) this.stopOne(cue.key);
      try {
        const handle = this.port.play({ ...cue, offsetMs: playheadMs - cue.startMs, initialGain: gain });
        this.active.set(cue.key, { cue, handle });
      } catch (e) {
        console.warn('[audio] port play failed', e);
      }
    }
    for (const key of Array.from(this.active.keys())) {
      if (!wanted.has(key)) this.stopOne(key);
    }
  }

  /**
   * Playback halted while the scene still waits (a choice panel): looping
   * cues keep playing at full gain, others play to their natural end.
   */
  hold(): void {
    this.active.forEach(({ cue, handle }) => {
      if (cue.loop) this.safe(() => this.port.setGain(handle, cue.gain));
    });
    this.lastPlayhead = null;
  }

  /** Stop every cue (pause, stop, scene end, leaving the editor). */
  stopAll(): void {
    for (const key of Array.from(this.active.keys())) this.stopOne(key);
    this.lastPlayhead = null;
  }

  /** Keys of the cues currently playing. */
  activeKeys(): string[] {
    return Array.from(this.active.keys());
  }

  private stopOne(key: string): void {
    const running = this.active.get(key);
    this.active.delete(key);
    if (running) this.safe(() => this.port.stop(running.handle));
  }

  /** A failing port never breaks playback. */
  private safe<T>(fn: () => T): T | undefined {
    try {
      return fn();
    } catch (e) {
      console.warn('[audio] port call failed', e);
      return undefined;
    }
  }
}
